// @tennis-matches/ui/tournament-setup/PlayerInput
// Player Autocomplete Component

import React, { useState, useEffect, useRef } from "react";
import "./player-autocomplete.css";

/**
 * PlayerAutocomplete Component
 * Provides autocomplete functionality for player name input
 *
 * @param {Object} props
 * @param {string} props.value - Current input value
 * @param {Function} props.onChange - Callback when input changes
 * @param {Function} props.onSelect - Callback when suggestion is selected
 * @param {Function} props.onSearch - Callback for search (returns Promise<Array>)
 * @param {Array} props.suggestions - Array of suggestion objects {id, name}
 * @param {string} props.placeholder - Input placeholder
 * @param {boolean} props.isRequired - Whether field is required
 * @param {string} props.inputId - HTML id for input element
 * @param {string} props.className - Additional CSS class
 * @param {Object} props.style - Additional inline styles
 * @param {Function} props.onBlur - Callback when input loses focus
 * @param {Function} props.onDelete - Callback when delete button is clicked (playerId) => Promise<void>
 * @param {boolean} [props.optionalField] – Mobile: dezenter Platzhalter (data-optional)
 */
export function PlayerAutocomplete({
  value,
  onChange,
  onSelect,
  onSearch,
  suggestions = [],
  placeholder = "Name eingeben oder aus Liste wählen",
  isRequired = false,
  optionalField = false,
  inputId = "player-input",
  className = "",
  style = {},
  onBlur = null,
  onDelete = null,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reset highlighted index when suggestions change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [suggestions]);

  // Aggressively remove name attribute and prevent iOS address book autocomplete
  useEffect(() => {
    const checkAndRemoveName = () => {
      if (inputRef.current) {
        // Remove name attribute if it exists (iOS uses it for address book)
        if (inputRef.current.hasAttribute("name")) {
          inputRef.current.removeAttribute("name");
        }
        // Namen brauchen Leerzeichen (Vor- + Nachname) → normales Text-Feld.
        // Adressbuch-Unterdrückung läuft über autocomplete/data-Attribute, nicht über type=url.
        if (inputRef.current.getAttribute("autocomplete") !== "off") {
          inputRef.current.setAttribute("autocomplete", "off");
        }
        if (inputRef.current.getAttribute("inputMode") !== "text") {
          inputRef.current.setAttribute("inputMode", "text");
        }
        if (inputRef.current.getAttribute("type") !== "text") {
          inputRef.current.setAttribute("type", "text");
        }
        // Remove any iOS-specific attributes that might trigger address book
        inputRef.current.removeAttribute("x-webkit-speech");
        inputRef.current.removeAttribute("x-webkit-grammar");
      }
    };

    // Check immediately
    checkAndRemoveName();

    // Check more frequently (iOS is very persistent)
    const interval = setInterval(checkAndRemoveName, 50);

    return () => clearInterval(interval);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    const input = e.target;

    // Remove readonly when user presses any key (iOS autocomplete prevention)
    if (input.hasAttribute("readonly")) {
      input.removeAttribute("readonly");
      input._isReadonlyForAutocomplete = false;
      input.setAttribute("inputMode", "text");
    }

    // Ensure name attribute is removed (iOS uses it for address book)
    input.removeAttribute("name");
    input.setAttribute("name", "");

    if (!isOpen || suggestions.length === 0) {
      if (e.key === "ArrowDown" && suggestions.length > 0) {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case "Enter":
        e.preventDefault();
        if (suggestions[highlightedIndex]) {
          handleSelect(suggestions[highlightedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  const handleInputChange = (e) => {
    const input = e.target;

    // Remove readonly when user actually types (iOS autocomplete prevention)
    if (input.hasAttribute("readonly")) {
      input.removeAttribute("readonly");
      input._isReadonlyForAutocomplete = false;
      input.setAttribute("inputMode", "text");
    }

    // Ensure name attribute is removed (iOS uses it for address book)
    input.removeAttribute("name");
    input.setAttribute("name", "");

    const newValue = e.target.value;
    onChange(newValue);

    if (newValue.trim().length >= 2) {
      onSearch?.(newValue);
      setIsOpen(true);
      setHighlightedIndex(0);
    } else {
      setIsOpen(false);
    }
  };

  const handleSelect = (suggestion) => {
    if (suggestion && suggestion.name) {
      // Close dropdown first to prevent blur issues
      setIsOpen(false);

      // Update input value immediately for visual feedback
      onChange(suggestion.name);

      // Keep focus on input to prevent blur
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          // Set the value directly on the input element as well
          inputRef.current.value = suggestion.name;
        }

        setTimeout(() => {
          if (onSelect && typeof onSelect === "function") {
            onSelect(suggestion);
          }
        }, 10);
      });
    }
  };

  const handleBlur = (e) => {
    const relatedTarget = e.relatedTarget || document.activeElement;
    const clickedOnSuggestion = wrapperRef.current?.contains(relatedTarget);
    if (clickedOnSuggestion) return;

    setTimeout(() => {
      setIsOpen(false);
      if (onBlur) onBlur(e);
    }, 300);
  };

  const isFilled = value && value.trim() && value.trim() !== "(Optional)";

  const handleFocus = (e) => {
    if (value && value.trim().length >= 2 && suggestions.length > 0) {
      setIsOpen(true);
    }
    const input = e.target;
    input.removeAttribute("name");
    input.setAttribute("name", "");
    input.setAttribute("readonly", "readonly");
    input._isReadonlyForAutocomplete = true;
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (input === document.activeElement) {
          input.removeAttribute("readonly");
          input._isReadonlyForAutocomplete = false;
          input.focus();
        }
      }, 10);
    });
    input.setAttribute("autocomplete", "off");
    input.setAttribute("inputMode", "text");
    input.removeAttribute("x-webkit-speech");
    input.removeAttribute("x-webkit-grammar");
    input.setAttribute("form", "");
    input.style.caretColor = "#000000";
    /* Kein blauer Rahmen am Input – Zustand zeigt die umgebende Namensbox (z. B. Wizard: grün bei ausgefüllt) */
    if (value && value.trim().length >= 2 && suggestions.length > 0) {
      setIsOpen(true);
    }
  };

  return (
    <div
      ref={wrapperRef}
      className="player-autocomplete"
      style={{ position: "relative", width: "100%", ...style }}
      // Prevent Safari from recognizing this as a form field
      data-safari-autocomplete="off"
    >
      {/* Hidden form to distract iOS from the real input */}
      <form
        style={{
          position: "absolute",
          left: "-9999px",
          opacity: 0,
          pointerEvents: "none",
          width: "1px",
          height: "1px",
        }}
        autoComplete="off"
        aria-hidden="true"
      >
        <input
          type="text"
          name="name"
          autoComplete="name"
          value=""
          readOnly
          tabIndex={-1}
        />
        <input
          type="text"
          name="given-name"
          autoComplete="given-name"
          value=""
          readOnly
          tabIndex={-1}
        />
      </form>

      {/* Hidden dummy input to confuse Safari autocomplete */}
      <input
        type="text"
        name="name"
        style={{
          position: "absolute",
          left: "-9999px",
          opacity: 0,
          pointerEvents: "none",
          width: "1px",
          height: "1px",
        }}
        tabIndex={-1}
        autoComplete="name"
        readOnly
        aria-hidden="true"
      />
      <input
        ref={inputRef}
        type="text"
        id={inputId}
        value={value || ""}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        // Namen (Vor- + Nachname) brauchen Leerzeichen → normales Text-Feld statt type=url.
        // Adressbuch/Passwortmanager werden über autocomplete=off + data-Attribute unterdrückt.
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="words"
        spellCheck="false"
        inputMode="text"
        // Safari/iOS specific (as data attributes since they're non-standard)
        data-x-autocomplete="off"
        data-webkit-autocomplete="off"
        // Password manager / LastPass / 1Password prevention
        data-lpignore="true"
        data-form-type="other"
        data-1p-ignore="true"
        data-bwignore="true"
        // Additional iOS/Safari prevention
        data-safari-autocomplete="off"
        data-ios-autocomplete="off"
        data-addressbook="off"
        data-autocomplete="off"
        data-contacts="off"
        // ARIA attributes to prevent browser interference
        role="textbox"
        aria-autocomplete="list"
        aria-haspopup="listbox"
        aria-label={placeholder}
        // Additional Safari hacks
        tabIndex={0}
        // Prevent iOS from recognizing this as a contact field
        name=""
        form=""
        data-optional={optionalField ? "true" : undefined}
        className={className}
        style={{
          width: "100%",
          padding: "0.75rem",
          paddingRight: isFilled ? "2.5rem" : "0.75rem", // Make room for clear button
          borderRadius: "0.5rem",
          fontSize: "1rem",
          transition: "all 0.15s ease",
          outline: "none",
          // Additional Safari-specific CSS to prevent autocomplete overlay
          WebkitAppearance: "none",
          WebkitUserSelect: "text",
          // Prevent iOS from showing address book suggestions
          WebkitTapHighlightColor: "transparent",
          // Aggressive iOS autocomplete prevention
          WebkitTextFillColor: "#000000",
          WebkitBoxShadow: "none",
          // Prevent iOS from showing autocomplete overlay
          appearance: "none",
          // Additional iOS-specific CSS hacks
          WebkitUserModify: "read-write-plaintext-only",
          // Prevent iOS from recognizing this as a contact field
          WebkitTouchCallout: "none",
          // Disable iOS text selection gestures that might trigger autocomplete
          userSelect: "text",
          // Force text rendering to prevent iOS from detecting name patterns
          textRendering: "optimizeLegibility",
          // Default styles (Wizard überschreibt mit rahmenlosem Input; keine blaue Umrandung)
          border: isFilled ? "1px solid transparent" : "1px solid #d1d5db",
          backgroundColor: isFilled ? "transparent" : "#ffffff",
          // Ensure cursor is always visible - use black for maximum contrast
          caretColor: "#000000",
          // Merge any additional styles from props (overrides defaults)
          ...style,
        }}
      />

      {/* Clear button (X) - only visible when field has content */}
      {isFilled && (
        <button
          type="button"
          className="player-autocomplete__clear-btn"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onChange("");
            if (inputRef.current) {
              inputRef.current.focus();
            }
          }}
          onMouseDown={(e) => {
            e.preventDefault(); // Prevent input blur
            e.stopPropagation();
          }}
          style={{
            position: "absolute",
            right: "0.5rem",
            top: "50%",
            transform: "translateY(-50%)",
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "0.25rem",
            borderRadius: "0.25rem",
            color: "#6b7280",
            fontSize: "1.25rem",
            fontWeight: "bold",
            lineHeight: "1",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "24px",
            height: "24px",
            transition: "all 0.15s ease",
            opacity: 0.7,
            zIndex: 10,
          }}
          onMouseEnter={(e) => {
            e.target.style.opacity = "1";
            e.target.style.backgroundColor = "#f3f4f6";
            e.target.style.color = "#111827";
          }}
          onMouseLeave={(e) => {
            e.target.style.opacity = "0.7";
            e.target.style.backgroundColor = "transparent";
            e.target.style.color = "#6b7280";
          }}
          aria-label="Feld leeren"
          title="Feld leeren"
        >
          ×
        </button>
      )}

      {/* Autocomplete dropdown – Klasse für Wizard-Dark-Theme (lesbare Vorschläge) */}
      {isOpen && suggestions.length > 0 && (
        <div
          className="player-autocomplete-dropdown player-autocomplete__dropdown"
          role="listbox"
          aria-label={placeholder}
          onMouseDown={(e) => {
            // Prevent input blur when clicking inside dropdown
            e.preventDefault();
          }}
          style={{
            backgroundColor: "#ffffff",
            border: "1px solid #d1d5db",
            pointerEvents: "auto",
          }}
        >
          {suggestions.map((suggestion, index) => {
            const handleDelete = async (e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!suggestion.id || !onDelete) return;
              try {
                await onDelete(suggestion.id);
                if (value && value.trim().length >= 2) onSearch?.(value);
              } catch (_) {}
            };

            return (
              <div
                key={suggestion.id || index}
                className="player-autocomplete-dropdown__item"
                role="option"
                aria-selected={index === highlightedIndex}
                onMouseDown={(e) => {
                  if (e.target.closest(".delete-button")) return;
                  e.preventDefault();
                  e.stopPropagation();
                  handleSelect(suggestion);
                }}
                onClick={(e) => {
                  if (e.target.closest(".delete-button")) return;
                  e.preventDefault();
                  e.stopPropagation();
                  handleSelect(suggestion);
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
                style={{
                  cursor: "pointer",
                  backgroundColor:
                    index === highlightedIndex ? "#eff6ff" : "transparent",
                  borderBottom:
                    index < suggestions.length - 1
                      ? "1px solid #f3f4f6"
                      : "none",
                  transition: "background-color 0.15s ease",
                  userSelect: "none", // Prevent text selection
                  WebkitUserSelect: "none",
                  color: "#111827",
                }}
              >
                <div className="player-autocomplete__name">
                  {suggestion.name}
                </div>
                {suggestion.caption ? (
                  <div
                    className="player-autocomplete__caption"
                    style={{
                      color: "#6b7280",
                      fontSize: "0.75rem",
                      marginTop: "0.125rem",
                    }}
                  >
                    {suggestion.caption}
                  </div>
                ) : null}
                {onDelete && suggestion.id && (
                  <button
                    type="button"
                    className="delete-button"
                    onClick={handleDelete}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "0.25rem 0.5rem",
                      borderRadius: "0.25rem",
                      color: "#ef4444",
                      fontSize: "1.125rem",
                      fontWeight: "bold",
                      lineHeight: "1",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: "24px",
                      minHeight: "24px",
                      transition: "all 0.15s ease",
                      opacity: 0.7,
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.opacity = "1";
                      e.target.style.backgroundColor = "#fee2e2";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.opacity = "0.7";
                      e.target.style.backgroundColor = "transparent";
                    }}
                    aria-label={`${suggestion.name} löschen`}
                    title={`${suggestion.name} löschen`}
                  >
                    ×
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
